"use client";

import type { FormVariant } from "@homeinstead/analytics";
import type { Rating as OfficeRating } from "@homeinstead/schemas";
import type { PortableTextBlock } from "next-sanity";
import type React from "react";
import type {
  CareSeekerLeadFormResponse,
  CareSeekerShallowLeadFormData,
  LeadSource,
} from "~www/types/forms/care-seeker-shallow-lead-form";
import type { LeadResponse } from "~www/types/forms/lead-response";
import type { CareOptionType } from "../shared/form-options";
import type { HeroVariant } from "../shared/hero-variants";

import { careSeekerContractSchema } from "@homeinstead/forms/schemas";
import { cn } from "@homeinstead/ui";
import Stepper from "@homeinstead/ui/components/Stepper";
import { zodResolver } from "@hookform/resolvers/zod";
import { useActionState, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { submitCareSeekerShallowForm } from "~www/actions/submitCareSeekerShallowForm";
import { useCareSeekerSubmissionResult } from "~www/hooks/useCareSeekerSubmissionResult";
import { useCareSeekerSubmissionTracking } from "~www/hooks/useCareSeekerSubmissionTracking";
import { useHandleFormSubmit } from "~www/hooks/useHandleFormSubmit";
import { useLeadFormTrackingHandlers } from "~www/hooks/useLeadFormTrackingHandlers";
import { getCareSeekerDefaultValues } from "../get-care-seeker-default-values";
import CareOptionStep from "../shared/CareOptionStep";
import { ContactStep } from "../shared/ContactStep";
import { getHeroVariantStyles } from "../shared/hero-variants";
import ThankYou from "../shared/ThankYou";
import CareRecipientStep from "./CareRecipientStep";

const ERROR_CONTEXT = "CareSeekerLeadForm";

interface CareSeekerLocalLeadFormV1Props
  extends React.FormHTMLAttributes<HTMLFormElement> {
  currentStep: number;
  handleBack: () => void;
  handleNext: () => void;
  finalStep: number;
  isDebug?: boolean;

  leadSource: LeadSource;

  // From sanity
  heroHeading: PortableTextBlock[];
  placeholders?: Record<string, string>;
  franchiseId: number;
  location: {
    name: string;
    postalCode: string;
  };
  officeRating?: OfficeRating | null;
  phoneNumber: string;
  variant?: HeroVariant;
  formVariant?: FormVariant;
}

const CareSeekerLocalLeadFormV1 = ({
  className,
  currentStep,
  handleBack,
  handleNext,
  finalStep,
  isDebug,
  heroHeading,
  placeholders,
  location,
  officeRating,
  franchiseId,
  leadSource,
  phoneNumber,
  variant = "default",
  formVariant = "v1",
  ...props
}: CareSeekerLocalLeadFormV1Props) => {
  const { textColorClass } = getHeroVariantStyles(variant);

  const methods = useForm<CareSeekerShallowLeadFormData>({
    defaultValues: getCareSeekerDefaultValues({
      franchiseId,
      postalCode: location.postalCode,
    }),
    mode: "onSubmit",
    resolver: zodResolver(careSeekerContractSchema),
    shouldUnregister: false,
  });

  const { getValues } = methods;

  const [state, submitFormAction, isPending] = useActionState(
    submitCareSeekerShallowForm,
    null,
  ) as [
    LeadResponse<CareSeekerLeadFormResponse> | null,
    typeof submitFormAction,
    boolean,
  ];

  const handleFormSubmit =
    useHandleFormSubmit<CareSeekerShallowLeadFormData>(methods);

  const [
    careFor,
    careType,
    organizationNumber,
    firstName,
    lastName,
    email,
    phone,
  ] = getValues([
    "careFor",
    "careType",
    "organizationNumber",
    "firstName",
    "lastName",
    "email",
    "phone",
  ]);

  const { memoryCare, everydayTasks, mobilityAssistance, other } = careType;
  const careOptions: CareOptionType[] = useMemo(
    () =>
      [
        memoryCare && "memoryCare",
        everydayTasks && "everydayTasks",
        mobilityAssistance && "mobilityAssistance",
        other && "other",
      ].filter(Boolean) as CareOptionType[],
    [memoryCare, everydayTasks, mobilityAssistance, other],
  );

  const { trackSuccess, trackError } = useLeadFormTrackingHandlers({
    careFor,
    careOptions,
    contactInfo: {
      email,
      firstName,
      lastName,
      phone,
    },
    formVariant,
    isDebug,
    organizationNumber,
  });

  const submissionOptions = {
    errorContext: ERROR_CONTEXT,
    firstName,
    getFirstName: () => getValues("firstName"),
    organizationNumber,
    trackError,
    trackSuccess,
  };
  useCareSeekerSubmissionTracking(state, submissionOptions);
  const submissionResult = useCareSeekerSubmissionResult(
    state,
    submissionOptions,
  );

  if (submissionResult) {
    return (
      <ThankYou firstName={submissionResult.firstName} variant={variant} />
    );
  }

  /**
   * Otherwise render form
   * Note: At the moment, some Steps are rendered in a Suspense boundary because each Step calls useSearchParams
   * via useSetQueryParam hook. This is a Next.js requirement.
   */
  return (
    <FormProvider {...methods}>
      <form
        action={submitFormAction}
        className={cn(
          className,
          "care-seeker-lead-form",
          "flex h-full flex-col items-start pt-8 md:items-center xl:items-start",
        )}
        onSubmit={handleFormSubmit}
        {...props}
      >
        <Stepper
          className="mx-auto w-full md:w-[440px] xl:ml-0"
          currentStep={currentStep - 1}
          handleBack={handleBack}
          showBackButton={currentStep > 2}
          textColorClass={textColorClass}
          totalSteps={finalStep - 2}
        />
        <input name="leadSource" type="hidden" value={leadSource} />
        <input name="formVariant" type="hidden" value={formVariant} />

        {/**
         * Step 2 - who needs care?
         */}
        <CareRecipientStep
          className={currentStep === 2 ? "flex" : "hidden"}
          formVariant={formVariant}
          //   isDebug={isDebug}
          heroHeading={heroHeading}
          nextStep={handleNext}
          officeRating={officeRating}
          phoneNumber={phoneNumber}
          placeholders={placeholders}
          variant={variant}
        />
        {/**
         * Step 3 - kinds of assistance needed
         */}
        <CareOptionStep
          className={currentStep === 3 ? "flex" : "hidden"}
          ctaVariant="primary"
          formVariant={formVariant}
          nextStep={handleNext}
          variant={variant}
        />

        <input name="organizationNumber" type="hidden" value={franchiseId} />
        <input name="postalCode" type="hidden" value={location.postalCode} />
        <input name="location" type="hidden" value={location.postalCode} />

        {/**
         * Step 4 - Contact information
         */}
        <ContactStep
          className={currentStep === 4 ? "flex" : "hidden"}
          isPending={isPending}
          variant={variant}
        />
      </form>
    </FormProvider>
  );
};

export default CareSeekerLocalLeadFormV1;
